import { useState, useEffect, useCallback } from "react";

// ── helpers ───────────────────────────────────────────────────────────────────
function safe(v) {
  return v !== null && v !== undefined && !isNaN(v);
}

function fmtNum(v, decimals = 2) {
  if (!safe(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(2) + "M";
  if (Math.abs(n) >= 1000) return n.toLocaleString("ko-KR", { maximumFractionDigits: decimals });
  return n.toFixed(decimals);
}

function fmtChange(v, pct, unit = "") {
  if (!safe(v)) return null;
  const n = Number(v);
  const sign = n >= 0 ? "+" : "";
  if (safe(pct)) return `${sign}${n.toFixed(2)} (${sign}${Number(pct).toFixed(2)}%)`;
  return `${sign}${n.toFixed(2)}${unit}`;
}

const FG_INFO = (v) => {
  if (v === null) return { label: "—", color: "#6b7280" };
  if (v >= 75) return { label: "극단적 탐욕", color: "#22c55e" };
  if (v >= 55) return { label: "탐욕",        color: "#86efac" };
  if (v >= 45) return { label: "중립",        color: "#eab308" };
  if (v >= 25) return { label: "공포",        color: "#f97316" };
  return              { label: "극단적 공포",  color: "#ef4444" };
};

// ── Sparkline ─────────────────────────────────────────────────────────────────
function Sparkline({ data, up }) {
  if (!data || data.length < 2) return <div style={{ height: 36 }} />;
  const vals = data.map((d) => d.v).filter((v) => v !== null);
  if (vals.length < 2) return <div style={{ height: 36 }} />;
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const W = 120, H = 36;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const color = up === null ? "#eab308" : up ? "#22c55e" : "#ef4444";
  const id = `sg${Math.random().toString(36).slice(2,6)}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: W, height: H }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${H} ${pts} ${W},${H}`} fill={`url(#${id})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ── Market Row ────────────────────────────────────────────────────────────────
function MarketRow({ label, sublabel, value, change, changePct, history, decimals = 2, unit = "", isFG = false, isKRW = false, invertColor = false }) {
  const up = change === null ? null : change >= 0;
  // invertColor=true인 지표(VIX)는 오르면 빨강, 내리면 초록
  const upColor = up === null ? "#6b7280" : (invertColor ? !up : up) ? "#22c55e" : "#ef4444";
  const sparkUp = up === null ? null : invertColor ? !up : up;
  const fg = isFG ? FG_INFO(value) : null;

  return (
    <div style={{
      display: "flex", alignItems: "center",
      padding: "14px 20px",
      borderBottom: "1px solid #0f172a",
      gap: 12,
      transition: "background 0.2s",
    }}
    onMouseEnter={(e) => e.currentTarget.style.background = "#0f172a"}
    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      {/* Label */}
      <div style={{ width: 90, flexShrink: 0 }}>
        <div style={{ color: "#ffffff", fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em" }}>{label}</div>
        {sublabel && <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 1 }}>{sublabel}</div>}
      </div>

      {/* Sparkline */}
      <div style={{ flexShrink: 0 }}>
        <Sparkline data={history} up={sparkUp} />
      </div>

      {/* Value + Change */}
      <div style={{ flex: 1, textAlign: "right" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 6 }}>
          <span style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "-0.02em" }}>
            {safe(value) ? (isKRW ? Number(value).toLocaleString("ko-KR", { maximumFractionDigits: 1 }) : fmtNum(value, decimals)) : "—"}
          </span>
          {unit && <span style={{ color: "#475569", fontSize: 11 }}>{unit}</span>}
        </div>

        {isFG ? (
          <div style={{ color: fg.color, fontSize: 11, marginTop: 2, fontWeight: 600 }}>{fg.label}</div>
        ) : (
          <div style={{ color: safe(change) ? upColor : "#6b7280", fontSize: 11, marginTop: 2, fontFamily: "'IBM Plex Mono', monospace" }}>
            {safe(change) ? fmtChange(change, changePct, unit) : "—"}
          </div>
        )}
      </div>

      {/* Up/Down dot */}
      <div style={{
        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
        background: upColor,
        boxShadow: up !== null ? `0 0 6px ${upColor}` : "none",
      }} />
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────────────────────
function SectionHeader({ title }) {
  return (
    <div style={{
      padding: "10px 20px 6px",
      color: "#64748b",
      fontSize: 11,
      letterSpacing: "0.15em",
      textTransform: "uppercase",
      fontFamily: "'IBM Plex Mono', monospace",
      borderBottom: "1px solid #0f172a",
    }}>{title}</div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState("");
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); setShowBanner(true); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") { setShowBanner(false); setInstallPrompt(null); }
  };

  const loadData = useCallback(async () => {
    try {
      const res = await fetch("/api/market");
      if (!res.ok) throw new Error(`API ${res.status}`);
      const json = await res.json();
      setData(json);
      setLastUpdate(new Date());
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const id = setInterval(loadData, 60 * 1000); // 1분 갱신
    return () => clearInterval(id);
  }, [loadData]);

  const d = data;

  return (
    <div style={{
      minHeight: "100vh",
      background: "#060b13",
      color: "#f1f5f9",
      fontFamily: "'Pretendard', 'Noto Sans KR', sans-serif",
      maxWidth: 480,
      margin: "0 auto",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&display=swap" rel="stylesheet" />

      {/* Install Banner */}
      {showBanner && (
        <div style={{
          background: "linear-gradient(135deg,#0f2027,#1a2a1a)", borderBottom: "1px solid #22c55e30",
          padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>📲</span>
          <div style={{ flex: 1, fontSize: 12 }}>
            <div style={{ color: "#f1f5f9", fontWeight: 700 }}>홈 화면에 설치</div>
            <div style={{ color: "#64748b", fontSize: 10 }}>앱처럼 빠르게 실행</div>
          </div>
          <button onClick={handleInstall} style={{
            background: "#22c55e", border: "none", borderRadius: 7,
            color: "#000", fontSize: 12, fontWeight: 700, padding: "6px 14px", cursor: "pointer",
          }}>설치</button>
          <button onClick={() => setShowBanner(false)} style={{
            background: "transparent", border: "none", color: "#475569", fontSize: 16, cursor: "pointer",
          }}>✕</button>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: "20px 20px 14px",
        borderBottom: "1px solid #0f172a",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 800, letterSpacing: "-0.03em", color: "#f1f5f9" }}>
            마켓 대시보드
          </h1>
          <div style={{ color: "#334155", fontSize: 10, marginTop: 3, fontFamily: "'IBM Plex Mono', monospace" }}>
            {lastUpdate ? lastUpdate.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"} · 1분 갱신
          </div>
        </div>
        <button onClick={loadData} style={{
          background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8,
          color: loading ? "#22c55e" : "#475569", fontSize: 16,
          width: 34, height: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "color 0.3s",
        }}>
          {loading ? "⟳" : "↺"}
        </button>
      </div>

      {error && (
        <div style={{ background: "#1c0a0a", padding: "8px 20px", color: "#ef4444", fontSize: 11 }}>
          ⚠ {error}
        </div>
      )}

      {/* 국내 주식 */}
      <SectionHeader title="국내 주식" />
      <MarketRow
        label="KOSPI" sublabel="코스피"
        value={d.kospi?.current} change={d.kospi?.change} changePct={d.kospi?.changePct}
        history={d.kospi?.history} decimals={2}
      />
      <MarketRow
        label="KOSDAQ" sublabel="코스닥"
        value={d.kosdaq?.current} change={d.kosdaq?.change} changePct={d.kosdaq?.changePct}
        history={d.kosdaq?.history} decimals={2}
      />

      {/* 미국 주식 */}
      <SectionHeader title="미국 주식" />
      <MarketRow
        label="NASDAQ" sublabel="나스닥"
        value={d.nasdaq?.current} change={d.nasdaq?.change} changePct={d.nasdaq?.changePct}
        history={d.nasdaq?.history} decimals={2}
      />
      <MarketRow
        label="S&P 500" sublabel="에스앤피"
        value={d.sp500?.current} change={d.sp500?.change} changePct={d.sp500?.changePct}
        history={d.sp500?.history} decimals={2}
      />
      <MarketRow
        label="DOW" sublabel="다우존스"
        value={d.dow?.current} change={d.dow?.change} changePct={d.dow?.changePct}
        history={d.dow?.history} decimals={2}
      />
      <MarketRow
        label="SOX" sublabel="필라델피아 반도체"
        value={d.sox?.current} change={d.sox?.change} changePct={d.sox?.changePct}
        history={d.sox?.history} decimals={2}
      />

      {/* 환율 / 달러 */}
      <SectionHeader title="환율 · 달러" />
      <MarketRow
        label="USD/KRW" sublabel="원달러 환율"
        value={d.usdkrw?.current} change={d.usdkrw?.change} changePct={d.usdkrw?.changePct}
        history={d.usdkrw?.history} decimals={1} unit="₩" isKRW
      />
      <MarketRow
        label="DXY" sublabel="달러인덱스 ICE"
        value={d.dxy?.current} change={d.dxy?.change} changePct={d.dxy?.changePct}
        history={d.dxy?.history} decimals={2}
      />

      {/* 센티멘트 */}
      <SectionHeader title="시장 심리" />
      <MarketRow
        label="VIX" sublabel="공포지수"
        value={d.vix?.current} change={d.vix?.change} changePct={d.vix?.changePct}
        history={d.vix?.history} decimals={1} invertColor
      />
      <MarketRow
        label="Fear & Greed" sublabel="CNN 공포탐욕"
        value={d.feargreed?.current} change={d.feargreed?.change} changePct={null}
        history={d.feargreed?.history} decimals={1} isFG invertColor
      />

      {/* Footer */}
      <div style={{ padding: "16px 20px", color: "#1e293b", fontSize: 9, fontFamily: "'IBM Plex Mono', monospace" }}>
        Yahoo Finance · CNN Fear & Greed · 장 마감 후 데이터는 전일 종가
      </div>

      <style>{`
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        body { margin: 0; background: #060b13; }
      `}</style>
    </div>
  );
}
